import type { FC } from 'react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { LEASE } from '@makemydeal/dr-platform-types';
import type { AltDealStructureGridCell } from '@makemydeal/dr-platform-types';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { updateAltStructureLeaseTerm, updateAltStructureLeaseDownPayment } from '@makemydeal/dr-dash-store/actions/offerRedux';
import { PaymentStructureGrid } from '../PaymentStructureGrid';
import type { Slot, SlotStatus } from '../PaymentStructureGrid/types';

// Lease axes/cells from the shared offer-redux lease grid state — same source as the main-page lease grid.
const selectLeaseTerms = createSelector(
    [(state) => offerReduxSelectors.getPaymentGridTerms(state, LEASE)],
    (terms: (number | null)[]): number[] => terms.filter((t): t is number => t != null)
);

const selectLeaseDownPayments = createSelector(
    [(state) => offerReduxSelectors.getPaymentGridCashDown(state, LEASE)],
    // Cash-down can be fractional (e.g. 4030.80); round to match the dashboard (roundValue).
    (cashDowns: (number | null)[]): number[] => cashDowns.filter((d): d is number => d != null).map((d) => Math.round(d))
);

export const selectLeaseSlots = createSelector(
    [
        (state) => offerReduxSelectors.getPaymentGridItems(state, LEASE),
        (state) => offerReduxSelectors.getPaymentGridTerms(state, LEASE),
        (state) => offerReduxSelectors.getOffer(state)
    ],
    (rows: any[], terms: (number | null)[], offer: any): Slot[] => {
        // Match cells by (months, requestedDownPayment), not the grid term id: Payment Service floors a
        // sub-minimum down payment to the lender minimum and re-keys the term id, which a getTermId lookup
        // would miss. requestedDownPayment stays equal to the grid axis value, so it's a stable key.
        const recordByTermAndDownPayment = offerReduxSelectors.getLeaseRecordsByTermAndDownPayment(offer);

        return rows.flatMap((row) =>
            row.items.map((item: any, termIdx: number): Slot => {
                const term = terms[termIdx] as number;
                // raw = actual (possibly fractional) value for record lookup / retry / residual replay;
                // display = rounded (matches dashboard) for the row label and slot↔row matching.
                const rawDownPayment = row.val as number;
                const displayDownPayment = Math.round(rawDownPayment);
                const id = `lease-${term}-${displayDownPayment}`;
                const batchKey = `lease-dp-${rawDownPayment}`;

                // Use the keyed grid item when present, else the (term, requestedDownPayment) record
                // (fallback for cells emptied by a re-keyed/floored cash-down id).
                const termRecord = !item.isEmpty
                    ? offer?.terms?.byId?.[item.id]
                    : recordByTermAndDownPayment.get(offerReduxSelectors.leaseRecordKey(term, rawDownPayment));
                const monthlyPayment: number | undefined = item.isEmpty ? termRecord?.monthlyPayment : item.monthlyPayment;
                const lenderCode = item.isEmpty ? termRecord?.lenderCode : item.lenderCode;

                const status: SlotStatus = item.isLoading ? 'pending' : monthlyPayment != null ? 'ready' : 'timed-out';

                if (monthlyPayment == null) {
                    return { id, term, downPayment: displayDownPayment, dealType: 'lease', status, batchKey };
                }

                const moneyFactor = termRecord?.rateType === 'MoneyFactor' ? termRecord.sellRate : undefined;

                // Profit from the shared lease term record: PQ-driven `profitDetails`, else offer-node `profit`.
                // Only the total is shown (badge), and it already nets front/back/reserve. Hide when <= 0.
                const profit = termRecord?.profitDetails ?? termRecord?.profit;
                const profitTotal = profit?.totalProfit != null && profit.totalProfit > 0 ? profit.totalProfit : undefined;

                const data: AltDealStructureGridCell = {
                    id,
                    dealType: 'lease',
                    term,
                    // raw — the scenario is created at the deal's actual cash down (residuals replayed by key).
                    downPayment: rawDownPayment,
                    monthlyPayment,
                    lender: lenderCode ?? '',
                    moneyFactor,
                    ...(profitTotal !== undefined && { profitTotal })
                };

                return { id, term, downPayment: displayDownPayment, dealType: 'lease', status, batchKey, data };
            })
        );
    }
);

export const LeaseStructuresGrid: FC = () => {
    const dispatch = useDispatch();
    const terms = useSelector(selectLeaseTerms);
    const downPayments = useSelector(selectLeaseDownPayments);
    const leaseSlots = useSelector(selectLeaseSlots);

    // Lease edits write to the shared lease grid state (keeps slideout ↔ main-page grid in sync).
    // PaymentStructureGrid commits the full axis; diff out the one changed index and dispatch it.
    const handleTermsCommit = useCallback(
        (nextTerms: number[]) => {
            const key = nextTerms.findIndex((val, index) => val !== terms[index]);
            if (key >= 0) {
                dispatch(updateAltStructureLeaseTerm(key, nextTerms[key]));
            }
        },
        [dispatch, terms]
    );

    const handleDownPaymentsCommit = useCallback(
        (nextDownPayments: number[]) => {
            const key = nextDownPayments.findIndex((val, index) => val !== downPayments[index]);
            if (key >= 0) {
                dispatch(updateAltStructureLeaseDownPayment(key, nextDownPayments[key]));
            }
        },
        [dispatch, downPayments]
    );

    return (
        <PaymentStructureGrid
            title="Lease Structures"
            terms={terms}
            downPayments={downPayments}
            slots={leaseSlots}
            dealType="lease"
            testIdPrefix="lease-structures-grid"
            onTermsCommit={handleTermsCommit}
            onDownPaymentsCommit={handleDownPaymentsCommit}
        />
    );
};
